import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { QRCodeGenerator } from "@/components/admin/QRCodeGenerator";
import { QRCodesList } from "@/components/admin/QRCodesList";
import { PaperBackupGenerator } from "@/components/admin/PaperBackupGenerator";
import { GroupMeetingList } from "@/components/admin/dashboard/GroupMeetingList";
import { getGroupStatusLabel } from "@/utils/groupScheduleUtils";
import type { Group } from "@/types/groups";

interface GroupDetailsProps {
  group: Group;
  surveyResponses?: any[];
}

export const GroupDetails = ({ group, surveyResponses }: GroupDetailsProps) => {
  const isScheduleAccepted = !!group.schedule_accepted_at;
  const status = getGroupStatusLabel(group);
  
  // Define meetings based on assigned group
  const interventionMeetings = [
    { number: 1, title: "Att vara familj i nytt land och barnens fritid" },
    { number: 2, title: "Förskola och skola, pojkar och flickor" },
    { number: 3, title: "Hälsa och sjukvård" },
    { number: 4, title: "Föräldrars rättigheter och skyldigheter" },
    { number: 5, title: "Att vara tonårsförälder" },
  ];
  
  const controlMeetings = [
    { number: 1, title: "Tematräff" },
  ];
  
  const meetings = group.assigned_group === "intervention" 
    ? interventionMeetings 
    : group.assigned_group === "control"
      ? controlMeetings
      : [];

  if (!isScheduleAccepted) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{group.name || `Grupp ${group.id.slice(0, 8)}`}</CardTitle>
          <CardDescription>
            Acceptera schemat först för att få tillgång till gruppens verktyg.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-2">
            <Badge variant={status.variant}>{status.label}</Badge>
            <span className="text-sm text-muted-foreground">
              Väntar på bekräftelse av schema
            </span>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div>
            <CardTitle>{group.name || `Grupp ${group.id.slice(0, 8)}`}</CardTitle>
            <CardDescription>
              Hantera QR-koder, pappersenkäter och sessionsutvärderingar
            </CardDescription>
          </div>
          <Badge variant={status.variant}>{status.label}</Badge>
        </div>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="sessions">
          <TabsList className="grid w-full grid-cols-3">
            <TabsTrigger value="sessions">Sessioner</TabsTrigger>
            <TabsTrigger value="qr">QR-koder</TabsTrigger>
            <TabsTrigger value="paper">Pappersenkäter</TabsTrigger>
          </TabsList>
          
          <TabsContent value="sessions" className="mt-4">
            <GroupMeetingList 
              meetings={meetings} 
              surveyResponses={surveyResponses}
              groupId={group.id}
            />
          </TabsContent>
          
          <TabsContent value="qr" className="mt-4 space-y-6">
            <QRCodeGenerator groupId={group.id} group={group} />
            <QRCodesList groupId={group.id} />
          </TabsContent>
          
          <TabsContent value="paper" className="mt-4">
            <PaperBackupGenerator groupId={group.id} group={group} />
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};
